import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { value: String, showTooltip: Boolean };
  static targets = ["copyIcon", "successIcon", "tooltip"];

  copy(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.currentTarget;

    const copyValue = button.getAttribute("data-copy-button-copy-value") || "";

    const copyIcon = button.querySelector(".CopyButton--icon--copy");
    if (!copyIcon) {
      console.error("Copy icon not found", button);
      return;
    }

    const successIcon = button.querySelector(".icon-checkmark.CopyButton--icon") || this.createSuccessIcon(button, copyIcon);

    this.clearButtonTimeouts(button);

    if (!copyValue || copyValue.trim() === "") {
      console.warn("Copy value is empty", { copyValue, button });
      return;
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(copyValue)
        .then(() => this.showSuccessFeedback(button, copyIcon, successIcon))
        .catch((err) => {
          console.error("Clipboard write failed", err);
          alert("Something went wrong, please try again.");
        });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = copyValue;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();

      try {
        const success = document.execCommand("copy");
        if (success) {
          this.showSuccessFeedback(button, copyIcon, successIcon);
        } else {
          alert("Something went wrong, please try again.");
        }
      } catch (err) {
        console.error("execCommand copy failed", err);
        alert("Something went wrong, please try again.");
      }

      document.body.removeChild(textArea);
    }
  }

  createSuccessIcon(button, copyIcon) {
    const parent = copyIcon?.parentNode || button;
    const successIcon = document.createElement("span");
    successIcon.className = "icon icon-checkmark CopyButton--icon u-textGreen";
    successIcon.style.display = "none";
    parent.appendChild(successIcon);
    return successIcon;
  }

  clearButtonTimeouts(button) {
    const resetTimeout = button.dataset.resetTimeout;
    const animationTimeout = button.dataset.animationTimeout;

    if (resetTimeout) {
      clearTimeout(parseInt(resetTimeout, 10));
      delete button.dataset.resetTimeout;
    }
    if (animationTimeout) {
      clearTimeout(parseInt(animationTimeout, 10));
      delete button.dataset.animationTimeout;
    }
    if (button._tooltipTimeout) {
      clearTimeout(button._tooltipTimeout);
      delete button._tooltipTimeout;
    }
    
    const wrapper = button.closest(".CopyButton-wrapper");
    if (wrapper) {
      const tooltip = wrapper.querySelector(".CopyButton-tooltip");
      if (tooltip) {
        tooltip.classList.remove("CopyButton-tooltip--visible");
        tooltip.style.display = "none";
      }
    }
  }

  showSuccessFeedback(button, copyIcon, successIcon) {
    const allCopyIcons = button.querySelectorAll(".CopyButton--icon--copy");
    allCopyIcons.forEach((icon) => {
      icon.style.display = "none";
      icon.classList.remove("CopyButton--iconAnimateIn");
      icon.style.removeProperty("animation");
    });

    let currentSuccessIcon = button._successIcon || successIcon;

    if (!currentSuccessIcon || !currentSuccessIcon.parentNode) {
      const parent = copyIcon?.parentNode || button;
      currentSuccessIcon = document.createElement("span");
      currentSuccessIcon.className = "icon icon-checkmark CopyButton--icon u-textGreen";
      currentSuccessIcon.style.display = "none";
      parent.appendChild(currentSuccessIcon);
      button._successIcon = currentSuccessIcon;
    }

    const allSuccessIcons = button.querySelectorAll(".icon-checkmark.CopyButton--icon");
    allSuccessIcons.forEach((icon) => {
      if (icon !== currentSuccessIcon) icon.style.display = "none";
    });

    currentSuccessIcon.classList.remove("CopyButton--iconSuccess");
    currentSuccessIcon.style.animation = "none";
    currentSuccessIcon.style.display = "none";

    void currentSuccessIcon.offsetWidth;
    void currentSuccessIcon.offsetHeight;

    currentSuccessIcon.style.display = "inline";

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        currentSuccessIcon.style.animation = "";
        currentSuccessIcon.classList.add("CopyButton--iconSuccess");
      });
    });

    const showTooltipAttr = button.getAttribute("data-copy-button-show-tooltip-value");
    const showTooltip = showTooltipAttr === null || showTooltipAttr === "true";
    if (showTooltip) {
      const wrapper = button.closest(".CopyButton-wrapper");
      if (wrapper) {
        const tooltip = wrapper.querySelector(".CopyButton-tooltip");
        if (tooltip) {
          if (button._tooltipTimeout) {
            clearTimeout(button._tooltipTimeout);
          }

          tooltip.style.display = "block";
          tooltip.classList.add("CopyButton-tooltip--visible");

          button._tooltipTimeout = setTimeout(() => {
            tooltip.classList.remove("CopyButton-tooltip--visible");
            setTimeout(() => {
              tooltip.style.display = "none";
            }, 200);
          }, 2000);
        }
      }
    }

    const resetTimeout = setTimeout(() => {
      const allSuccessIcons2 = button.querySelectorAll(".icon-checkmark.CopyButton--icon");
      allSuccessIcons2.forEach((icon) => {
        icon.style.display = "none";
        icon.classList.remove("CopyButton--iconSuccess");
      });

      if (copyIcon) {
        const allCopyIcons2 = button.querySelectorAll(".CopyButton--icon--copy");
        allCopyIcons2.forEach((icon) => {
          icon.classList.remove("CopyButton--iconAnimateIn");
          icon.style.removeProperty("animation");
          icon.style.display = "none";
        });

        void copyIcon.offsetWidth;
        copyIcon.style.display = "inline";

        requestAnimationFrame(() => {
          copyIcon.classList.add("CopyButton--iconAnimateIn");

          const animationTimeout = setTimeout(() => {
            copyIcon.classList.remove("CopyButton--iconAnimateIn");
            delete button.dataset.animationTimeout;
          }, 300);

          button.dataset.animationTimeout = animationTimeout.toString();
        });
      }
      delete button.dataset.resetTimeout;
    }, 2000);

    button.dataset.resetTimeout = resetTimeout.toString();
  }
}
